import {
  Bot,
  Beaker,
  BookOpen,
  Settings,
  Cable,
  Plug,
  Puzzle,
  Radio,
  Play,
  MessageSquare,
  Bolt,
  Building2,
  KeyRound,
  Rocket,
  BarChart3,
} from "lucide-react";
import type { NavGroup } from "./nav-main";
import { OperatorMark } from "@/components/brand/OperatorMark";
import { UpdateAvailableNavItem } from "./UpdateAvailableNavItem";
import { routes } from "@/lib/routes";
import { recallPath } from "@/lib/last-path";

export type ConsoleNavRole = 'admin' | 'user' | 'viewer';

/**
 * Build the console sidebar groups for a given active space. Space-scoped
 * sections (Agents, Skills, …) live under `/console/spaces/:spaceId/...`;
 * Events and Settings are flat (cross-space / user-global). The Organization
 * group is admin-only and appears just above the Operator link.
 *
 * When `spaceId` is empty, space-scoped links are disabled — this happens
 * briefly while the user record loads on first paint.
 */
export function consoleNavGroups(
  spaceId: string,
  role: ConsoleNavRole | undefined = 'user',
): NavGroup[] {
  const hasSpace = spaceId.length > 0;
  const groups: NavGroup[] = [
    // Space-scoped sections — driven by the active space picked in the switcher.
    // Indented so they read as children of the Spaces section above.
    {
      indent: true,
      items: [
        { title: "Agents", href: hasSpace ? routes.console.spaceAgents(spaceId) : '#', icon: Bot, disabled: !hasSpace },
        { title: "Skills", href: hasSpace ? routes.console.spaceSkills(spaceId) : '#', icon: Beaker, disabled: !hasSpace },
        { title: "Plugins", href: hasSpace ? routes.console.spacePlugins(spaceId) : '#', icon: Puzzle, disabled: !hasSpace },
        { title: "Tools", href: hasSpace ? routes.console.spaceTools(spaceId) : '#', icon: Settings, disabled: !hasSpace },
        { title: "Knowledge", href: hasSpace ? routes.console.spaceKbs(spaceId) : '#', icon: BookOpen, disabled: !hasSpace },
        { title: "Connections", href: hasSpace ? routes.console.spaceConnectionEmails(spaceId) : '#', icon: Cable, disabled: !hasSpace },
        { title: "Integrations", href: hasSpace ? routes.console.spaceIntegrations(spaceId) : '#', icon: Plug, disabled: !hasSpace },
        { title: "Sandbox", href: hasSpace ? routes.console.spaceSandbox(spaceId) : '#', icon: MessageSquare, disabled: !hasSpace },
      ],
    },
    // Cross-space / user-global — separated visually so it's clear they
    // aren't filtered by the space switcher.
    {
      items: [
        { title: "Runs", href: routes.console.runs, icon: Play },
        { title: "Events", href: routes.console.events, icon: Radio },
        { title: "Credentials", href: routes.console.credentials, icon: KeyRound },
        { title: "Settings", href: routes.console.settings, icon: Bolt },
      ],
    },
  ];

  // Org-admin section. Only rendered for users with role === 'admin'; the
  // matching route guard (<RequireOrgAdmin>) is still authoritative.
  if (role === 'admin') {
    // Link to the org root rather than /integrations so the sidebar entry
    // stays highlighted on every /c/org/* page (including MCP servers and
    // any drill-down detail pages). The index route redirects to integrations.
    groups.push({
      items: [
        { title: "Analytics", href: routes.console.analytics, icon: BarChart3 },
        { title: "Organization", href: routes.console.org.root, icon: Building2 },
      ],
    });
  }

  groups.push({
    items: [
      // Sits above the Operator link, hidden unless CloudFront reports a
      // build different from the one this tab booted with — see
      // `src/lib/version-monitor.ts`. The `render` escape hatch lets the
      // entry stay out of the DOM entirely when no update is pending.
      {
        title: "Update available",
        href: "#",
        icon: Rocket,
        render: (collapsed) => <UpdateAvailableNavItem collapsed={collapsed} />,
      },
      {
        title: "Operator",
        href: () => recallPath("operator", routes.operator.root),
        icon: OperatorMark,
      },
    ],
  });

  return groups;
}
